import { useQuery } from "@apollo/client";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { WASTE_LINES_UNIFIED_QUERY } from "routes/Customer/Waste/ManifestLineItems/api";

import {
  CompareSourcesTable,
  toCompareSourcesTableRows,
} from "../../../Manifests/CompareSourcesTable";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { buildShippedDateRangeToken } from "./shippedDateRange";

// A stream's lines span many manifests; cap the comparison so a huge stream
// can't balloon the query. The user narrows the date range to see fewer.
const STREAM_COMPARE_LINE_CAP = 250;
// The scan fetches up to 500 lines solely to detect truncation: if the result
// exceeds 250 we set `truncated = true` and cap the ids. A stream with more
// than 500 lines in range is itself bounded by this page size, so `truncated`
// stays correct (>250) in all cases.
const LINE_SCAN_PAGE_SIZE = 500;

const COMPARE_WASTE_STREAM_SOURCES_QUERY = gql(`
  query CompareWasteStreamSources($wasteLineUnifiedIds: [ID!]!) {
    compareWasteStreamSources(wasteLineUnifiedIds: $wasteLineUnifiedIds) {
      availableSources
      differenceCount
      missingCount
      rows {
        key
        group
        field
        label
        eManifest
        vendor
        document
        differs
        missingInSomeSource
        availableSources
      }
    }
  }
`);

/**
 * Build the omnisearch string for the stream's line scan. Scope to whatever
 * range is set — a full range, or a single start/end bound (the standardized
 * date filters allow either) — via the shared `buildShippedDateRangeToken`.
 * With no range, compare the stream's lines across ALL time (omit the
 * `shippedDate` clause): an earlier current-year fallback silently hid
 * prior-year manifests, so a stream whose shipments are all in a past year read
 * as "none".
 */
export function buildStreamCompareSearch({
  tenantId,
  wasteStreamId,
  startDate,
  endDate,
}: {
  tenantId: string;
  wasteStreamId: string;
  startDate?: string | null;
  endDate?: string | null;
}): string {
  const token = buildShippedDateRangeToken(startDate ?? undefined, endDate ?? undefined);
  const dateFilter = token ? ` shippedDate:${token}` : "";
  return `tenantId:${tenantId} wasteStreamId:${wasteStreamId}${dateFilter}`;
}

/**
 * Cap a stream's line-item ids to {@link STREAM_COMPARE_LINE_CAP}. Returns the
 * capped slice plus whether the original set exceeded the cap (so the caller can
 * show the truncation notice). A stream at or under the cap is returned whole
 * with `truncated: false`.
 */
export function capStreamCompareLineIds(
  allIds: string[],
  cap = STREAM_COMPARE_LINE_CAP
): { cappedIds: string[]; truncated: boolean } {
  return { cappedIds: allIds.slice(0, cap), truncated: allIds.length > cap };
}

/**
 * Compare Data Sources for a whole waste stream: gathers the stream's line item
 * ids (date-filtered, capped) and renders the shared comparison table directly —
 * no manifest picker. The values are compared across e-Manifest, vendor, and
 * document sources for every line in the stream at once.
 */
export const WasteStreamCompareDialog = ({
  open,
  onClose,
  wasteStreamId,
  streamName,
}: {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
  streamName: string;
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { tenantId } = useTenant();
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { startDate, endDate } = deferredFilters;

  const search = useMemo(
    () => buildStreamCompareSearch({ tenantId: tenantId ?? "", wasteStreamId, startDate, endDate }),
    [tenantId, wasteStreamId, startDate, endDate]
  );

  // The 250-item cap is decided here, so the scan sort must be a TOTAL order —
  // otherwise which lines land inside the cap is DB-arbitrary at the boundary
  // (many lines can share a shippedDate). `shippedDate` alone isn't total: the
  // wasteLinesUnified regularSort path appends no tiebreaker. Add
  // manifestTrackingNumber then id (mirroring that resolver's raw
  // wasteLineNumber path, which trails id to "make the ordering total"), so a
  // truncated stream's contents are reproducible. Newest shipped first.
  const { data: linesData, loading: linesLoading } = useQuery(WASTE_LINES_UNIFIED_QUERY, {
    variables: {
      search,
      page: 0,
      pageSize: LINE_SCAN_PAGE_SIZE,
      sort: [
        { field: "shippedDate", sort: "desc" },
        { field: "manifestTrackingNumber", sort: "asc" },
        { field: "id", sort: "asc" },
      ],
    },
    skip: !open || !tenantId,
  });

  const allIds = useMemo(
    () => (linesData?.wasteLinesUnified?.items ?? []).map((l) => String(l.id)),
    [linesData]
  );
  const { cappedIds, truncated } = useMemo(() => capStreamCompareLineIds(allIds), [allIds]);

  const { data: compareData, loading: compareLoading } = useQuery(
    COMPARE_WASTE_STREAM_SOURCES_QUERY,
    { variables: { wasteLineUnifiedIds: cappedIds }, skip: !open || cappedIds.length === 0 }
  );

  const result = compareData?.compareWasteStreamSources;

  const rows = useMemo(() => toCompareSourcesTableRows(result?.rows ?? []), [result]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      noPrimaryAction
      title={`Compare data sources — ${streamName}`}
      contentSx={{ p: 0, overflow: "hidden" }}
      slotProps={{
        paper: { sx: { height: fullScreen ? "100%" : "90vh", maxHeight: "90vh" } },
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {truncated && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 2, py: 1, flexShrink: 0 }}
          >
            Showing the first {STREAM_COMPARE_LINE_CAP} line items — narrow the date range to see
            fewer.
          </Typography>
        )}
        {!linesLoading && allIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No line items to compare for this waste stream.
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, px: 2 }}>
            {/* When truncated, differenceCount/missingCount reflect only the
                capped line items; the truncation notice above communicates this. */}
            <CompareSourcesTable
              rows={rows}
              availableSources={result?.availableSources ?? []}
              differenceCount={result?.differenceCount ?? 0}
              missingCount={result?.missingCount ?? 0}
              loading={linesLoading || compareLoading}
              hasManifestGroup={false}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
